// @ts-nocheck
import { useEffect, useRef, useState, useCallback } from 'react';

import merkoLogo from '../assets/merko-logo.svg';
import blackMerkoLogoerkoLogo from '../assets/Merko_dark_Logo.svg';

import {
  signIn, signInWithRedirect,
  signUp, confirmSignUp, resendSignUpCode,
  resetPassword, confirmResetPassword,
} from 'aws-amplify/auth';
import {
  Eye, EyeOff,
  Mail, User, Lock, KeyRound,
  Zap, BarChart2, Users, LineChart,
  Star, AlertCircle, CheckCircle2, ArrowLeft,
} from 'lucide-react';
function useAuthViewportLock(shellRef, scrollRef) {
  useEffect(() => {
    const shell = shellRef.current;
    const handleTouchMove = (e) => {
      const scroller = scrollRef.current;
      if (!scroller) {
        e.preventDefault();
        return;
      }
      const target = e.target;
      if (!scroller.contains(target)) {
        e.preventDefault();
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;

      if (scrollHeight <= clientHeight) {
        e.preventDefault();
        return;
      }

      if (atTop && e.touches[0]) {
        if (typeof scroller._touchStartY === 'number') {
          const currentY = e.touches[0].clientY;
          const delta = currentY - scroller._touchStartY;
          if (atTop && delta > 0) {
            e.preventDefault();
            return;
          }
          if (atBottom && delta < 0) {
            e.preventDefault();
            return;
          }
        }
      }
    };

    const handleTouchStart = (e) => {
      const scroller = scrollRef.current;
      if (scroller && e.touches[0]) {
        scroller._touchStartY = e.touches[0].clientY;
      }
    };

    // Attach to document with { passive: false } to allow preventDefault
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: true });

    // ════════════════════════════════════════════════════════════════════════
    // B) VIRTUAL KEYBOARD HANDLING
    // ════════════════════════════════════════════════════════════════════════
    const vv = window.visualViewport;
    const THRESHOLD = 150;
    let isOpen = false;

    const update = () => {
      const currentShell = shellRef.current;
      if (!currentShell || !vv) return;

      const diff = window.innerHeight - vv.height;
      const keyboardNowOpen = diff > THRESHOLD;

      if (keyboardNowOpen) {
        if (!isOpen) {
          isOpen = true;
          currentShell.classList.add('merko-keyboard-open');
        }

        // Counteract iOS's upward visual viewport push
        const offset = vv.offsetTop;
        currentShell.style.setProperty('--vv-height', `${vv.height}px`);
        currentShell.style.transform = `translateY(${offset}px)`;

        // Scroll focused input into view
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
            const scroller = scrollRef.current;
            if (scroller) {
              const inputRect = active.getBoundingClientRect();
              const scrollerRect = scroller.getBoundingClientRect();
              const inputRelTop = inputRect.top - scrollerRect.top + scroller.scrollTop;
              const targetScroll = inputRelTop - (vv.height * 0.35);
              scroller.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
            }
          }
        });
      } else if (isOpen) {
        isOpen = false;
        currentShell.classList.remove('merko-keyboard-open');
        currentShell.style.setProperty('--vv-height', '100dvh');
        currentShell.style.transform = '';
      }
    };

    const handleFocusIn = (e) => {
      if (!isOpen) return;
      const active = e.target;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
        setTimeout(() => {
          update();
          const scroller = scrollRef.current;
          if (scroller) {
            requestAnimationFrame(() => {
              const inputRect = active.getBoundingClientRect();
              const scrollerRect = scroller.getBoundingClientRect();
              const inputRelTop = inputRect.top - scrollerRect.top + scroller.scrollTop;
              const targetScroll = inputRelTop - (vv.height * 0.35);
              scroller.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
            });
          }
        }, 100);
      }
    };

    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    document.addEventListener('focusin', handleFocusIn);

    // ════════════════════════════════════════════════════════════════════════
    // Cleanup
    // ════════════════════════════════════════════════════════════════════════
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', handleTouchStart);
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      document.removeEventListener('focusin', handleFocusIn);
      const currentShell = shellRef.current;
      if (currentShell) {
        currentShell.classList.remove('merko-keyboard-open');
        currentShell.style.transform = '';
      }
    };
  }, [shellRef, scrollRef]);
}

export function SplashScreen() {
  return (
    <div className="merko-splash">
      <img
        src={merkoLogo}
        alt="Merko"
        className='relative align-center justify-center w-[180px] h-[180px]'
      />
    </div>
  );
}

// ─── Premium Animated Canvas ─────────────────────────────────────────────────
function AnimatedCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, W, H;

    /* ── Particles ── */
    const PARTICLE_COUNT = 55;
    const particles = [];

    const ENVELOPES = [
      { px: 0.18, py: 0.22, size: 15, bobSpeed: 0.007, phase: 0.0, rotSpeed: 0.0002 },
      { px: 0.60, py: 0.38, size: 11, bobSpeed: 0.009, phase: 1.2, rotSpeed: -0.0003 },
      { px: 0.80, py: 0.18, size: 13, bobSpeed: 0.008, phase: 2.4, rotSpeed: 0.00025 },
      { px: 0.28, py: 0.70, size: 12, bobSpeed: 0.0075, phase: 0.8, rotSpeed: -0.0002 },
      { px: 0.72, py: 0.62, size: 14, bobSpeed: 0.0085, phase: 3.1, rotSpeed: 0.0003 },
      { px: 0.10, py: 0.76, size: 10, bobSpeed: 0.0095, phase: 1.7, rotSpeed: -0.00015 },
      { px: 0.88, py: 0.80, size: 11, bobSpeed: 0.0065, phase: 2.0, rotSpeed: 0.00022 },
    ];

    function drawEnvelope(cx, cy, size, alpha, rotation) {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rotation);
      ctx.globalAlpha = alpha;

      /* Body */
      const grd = ctx.createLinearGradient(-size, -size * 0.65, size, size * 0.65);
      grd.addColorStop(0, 'rgba(99,102,241,0.18)');
      grd.addColorStop(1, 'rgba(79,70,229,0.10)');
      ctx.fillStyle = grd;
      ctx.strokeStyle = `rgba(129,140,248,${alpha * 0.70})`;
      ctx.lineWidth = 1.2;
      ctx.shadowColor = 'rgba(99,102,241,0.45)';
      ctx.shadowBlur = 12;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(-size, -size * 0.65, size * 2, size * 1.3, 3);
      else ctx.rect(-size, -size * 0.65, size * 2, size * 1.3);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.stroke();

      /* V-flap */
      ctx.shadowColor = 'rgba(99,102,241,0.28)';
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.moveTo(-size, -size * 0.65);
      ctx.lineTo(0, size * 0.15);
      ctx.lineTo(size, -size * 0.65);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    /* ── Orbital ring with a glowing dot traveling along it ── */
    function drawOrbitalRing(t, ring) {
      const cx = W * 0.5, cy = H * 0.5;
      const rx = W * (0.42 + ring * 0.1);
      const ry = H * (0.30 + ring * 0.08);
      const angle = t * 0.0015 * (ring % 2 === 0 ? 1 : -1);

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);

      /* Ring */
      ctx.beginPath();
      ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(99,102,241,${0.08 - ring * 0.015})`;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 16]);
      ctx.stroke();
      ctx.setLineDash([]);

      /* Traveling glowing dot */
      const dotAngle = t * 0.008 * (ring % 2 === 0 ? 1 : -1);
      const dotX = Math.cos(dotAngle) * rx;
      const dotY = Math.sin(dotAngle) * ry;
      const dotPulse = Math.sin(t * 0.04) * 0.3 + 0.7;

      ctx.shadowColor = ring === 0 ? 'rgba(129,140,248,0.9)' : 'rgba(196,181,253,0.9)';
      ctx.shadowBlur = 14;
      ctx.beginPath();
      ctx.arc(dotX, dotY, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = ring === 0 ? `rgba(165,148,255,${dotPulse})` : `rgba(196,181,253,${dotPulse})`;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    }

    function initParticles() {
      particles.length = 0;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push({
          x: Math.random() * W, y: Math.random() * H,
          r: Math.random() * 1.8 + 0.4,
          vx: (Math.random() - 0.5) * 0.22,
          vy: (Math.random() - 0.5) * 0.22,
          alpha: Math.random() * 0.5 + 0.15,
          color: Math.random() > 0.5 ? '99,102,241' : '129,140,248',
          pulseOffset: Math.random() * Math.PI * 2,
        });
      }
    }

    /* Track envelope rotations */
    const envRotations = ENVELOPES.map(() => 0);

    let t = 0;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      t++;

      /* Connection lines — distance-gated, opacity fades with distance */
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 9800) { // 99px
            const dist = Math.sqrt(dist2);
            const fade = (1 - dist / 99) * 0.14;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(99,102,241,${fade})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      /* Particles */
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const pulse = Math.sin(t * 0.018 + p.pulseOffset) * 0.22 + 0.78;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha * pulse})`;
        ctx.fill();
      }

      /* Orbital rings */
      drawOrbitalRing(t, 0);
      drawOrbitalRing(t, 1);

      /* Slow-floating envelopes with gentle rotation */
      for (let i = 0; i < ENVELOPES.length; i++) {
        const env = ENVELOPES[i];
        envRotations[i] += env.rotSpeed;
        const ex = env.px * W;
        /* Slow bob: period ≈ 2π / (bobSpeed) frames ≈ 12-16 s at 60fps */
        const ey = env.py * H + Math.sin(t * env.bobSpeed + env.phase) * 12;
        const alpha = 0.45 + Math.sin(t * env.bobSpeed * 0.6 + env.phase) * 0.14;
        drawEnvelope(ex, ey, env.size, alpha, envRotations[i]);
      }

      animId = requestAnimationFrame(draw);
    }

    resize();
    initParticles();
    draw();

    const ro = new ResizeObserver(() => { resize(); initParticles(); });
    ro.observe(canvas);
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return (
    <canvas ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
  );
}

// ─── Hero Panel ───────────────────────────────────────────────────────────────
const FEATURES = [
  {
    Icon: Zap,
    title: 'AI Campaign Builder',
    desc: 'Generate full campaigns in seconds with intelligent copy, design, and targeting.',
    accent: '#818cf8',
  },
  {
    Icon: BarChart2,
    title: 'A/B Testing',
    desc: 'Automatically test variants and let AI pick the winner for you.',
    accent: '#34d399',
  },
  {
    Icon: Users,
    title: 'Smart Segmentation',
    desc: 'Reach the right audience with behaviour-driven audience segments.',
    accent: '#f472b6',
  },
  {
    Icon: LineChart,
    title: 'Live Analytics',
    desc: 'Real-time dashboards that surface the metrics that matter most.',
    accent: '#fb923c',
  },
];

export function HeroPanel() {
  return (
    <div className="merko-hero-panel">
      <AnimatedCanvas />
      <div className="merko-hero-overlay" />
      <div className="merko-hero-content">

        {/* Brand */}
        <div className="merko-brand">
          <img src={merkoLogo} alt="Merko" className="merko-brand-name"
            style={{ height: 30, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </div>

        {/* Copy */}
        <div className="merko-hero-copy">
          <div className="merko-hero-eyebrow">
            <span className="merko-eyebrow-dot" />
            Intelligent Email Marketing Platform
          </div>
          <h1 className="merko-hero-title">
            AI-Powered<br />
            Email Marketing<br />
            <span className="merko-hero-accent">That Converts</span>
          </h1>
          <p className="merko-hero-subtitle">
            Autonomous AI agents that design, send, and optimize your campaigns — 24/7, without lifting a finger.
          </p>
        </div>

        {/* Feature cards */}
        <div className="merko-stats-grid">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="merko-stat-card"
              style={{ animationDelay: `${i * 0.1}s`, '--stat-accent': f.accent }}>
              <div className="merko-stat-icon-wrap">
                <f.Icon size={15} strokeWidth={2} />
              </div>
              <div>
                <div className="merko-stat-value" style={{ fontSize: '0.78rem', letterSpacing: 0 }}>{f.title}</div>
                <div className="merko-stat-label">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

// ─── Input with left icon ─────────────────────────────────────────────────────
function IconInput({ icon: Icon, type = 'text', placeholder, value, onChange, autoComplete, required, maxLength, center }) {
  return (
    <div className="merko-icon-input-wrap">
      <span className="merko-icon-input-icon"><Icon size={15} strokeWidth={2} /></span>
      <input
        className={`merko-input merko-icon-input${center ? ' merko-input--center' : ''}`}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required={required}
        maxLength={maxLength}
      />
    </div>
  );
}

// ─── Password Input with icon ─────────────────────────────────────────────────
function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#ef4444' };
  if (score <= 2) return { score, label: 'Fair', color: '#f97316' };
  if (score <= 3) return { score, label: 'Good', color: '#eab308' };
  if (score <= 4) return { score, label: 'Strong', color: '#22c55e' };
  return { score, label: 'Very strong', color: '#10b981' };
}

function PasswordStrengthBar({ password }) {
  const { score, label, color } = getPasswordStrength(password);
  if (!password) return null;
  return (
    <div className="merko-strength-wrap">
      <div className="merko-strength-bars">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="merko-strength-bar"
            style={{
              background: i <= score ? color : '#e5e7eb',
              transition: `background 0.3s ${i * 0.05}s`
            }} />
        ))}
      </div>
      <span className="merko-strength-label" style={{ color }}>{label}</span>
    </div>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, showStrength }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="merko-icon-input-wrap merko-pass-wrap">
        <span className="merko-icon-input-icon"><Lock size={15} strokeWidth={2} /></span>
        <input
          className="merko-input merko-icon-input"
          type={show ? 'text' : 'password'}
          placeholder={placeholder || 'Enter your password'}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete || 'current-password'}
          required
        />
        <button type="button" className="merko-pass-toggle"
          onClick={() => setShow(s => !s)} aria-label="Toggle password">
          {show ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
        </button>
      </div>
      {showStrength && <PasswordStrengthBar password={value} />}
    </div>
  );
}

// ─── Google + Merko Logo Icons ───────────────────────────────────────────────
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

// ─── Main Auth Form ───────────────────────────────────────────────────────────
export function CustomSignIn() {
  const [tab, setTab] = useState('signIn');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  /* Sign In */
  const [siEmail, setSiEmail] = useState('');
  const [siPassword, setSiPassword] = useState('');

  /* Sign Up — mirrors original Amplify Authenticator fields */
  const [suUsername, setSuUsername] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [suConfirm, setSuConfirm] = useState('');

  /* Confirm sign-up */
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  /* Forgot password */
  const [fpView, setFpView] = useState(false);
  const [resetView, setResetView] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const clearErr = () => setError('');

  /* Resend cooldown */
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  /* ── Handlers ── */
  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!siEmail || !siPassword) { setError('Please enter your email and password.'); return; }
    setLoading(true); clearErr();
    try {
      await signIn({ username: siEmail.trim(), password: siPassword });
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!suUsername.trim()) { setError('Please enter a username.'); return; }
    if (suPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (suPassword !== suConfirm) { setError('Passwords do not match.'); return; }
    setLoading(true); clearErr();
    try {
      await signUp({ username: suUsername.trim(), password: suPassword });
      setShowConfirm(true);
    } catch (err) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    if (!confirmCode.trim()) { setError('Please enter the verification code.'); return; }
    setLoading(true); clearErr();
    try {
      await confirmSignUp({ username: suUsername.trim(), confirmationCode: confirmCode.trim() });
      await signIn({ username: suUsername.trim(), password: suPassword });
    } catch (err) {
      setError(err.message || 'Verification failed. Please check the code.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await resendSignUpCode({ username: suUsername.trim() });
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || 'Could not resend code.');
    }
  };

  const handleGoogle = async () => {
    try { await signInWithRedirect({ provider: 'Google' }); }
    catch (err) { setError(err.message || 'Google sign in failed.'); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!siEmail) { setError('Enter your email first.'); return; }
    setLoading(true); clearErr();
    try {
      await resetPassword({ username: siEmail.trim() });
      setFpView(false); setResetView(true);
    } catch (err) {
      setError(err.message || 'Could not send reset code.');
    } finally { setLoading(false); }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!resetCode || !newPassword) { setError('Please fill all fields.'); return; }
    setLoading(true); clearErr();
    try {
      await confirmResetPassword({ username: siEmail.trim(), confirmationCode: resetCode, newPassword });
      setResetSuccess(true);
      setTimeout(() => {
        setResetView(false); setResetSuccess(false);
        setFpView(false); setTab('signIn'); setSiPassword('');
      }, 2500);
    } catch (err) {
      setError(err.message || 'Reset failed. Check your code.');
    } finally { setLoading(false); }
  };

  const switchTab = (t) => {
    setTab(t); clearErr();
    setFpView(false); setResetView(false);
    setShowConfirm(false); setConfirmCode('');
  };

  const heading = fpView ? 'Reset password'
    : resetView ? 'Check your email'
      : showConfirm ? 'Verify your email'
        : tab === 'signIn' ? 'Welcome back'
          : 'Create your account';

  const subheading = fpView ? "We'll send a reset code to your inbox"
    : resetView ? (siEmail ? `Code sent to ${siEmail}` : 'Enter the code we sent to your email')
      : showConfirm ? 'Enter the 6-digit code sent to your email'
        : tab === 'signIn' ? 'Sign in to continue to Merko'
          : 'Start free — no credit card required';

  const titleGradient = true;
  const showTabs = !fpView && !resetView && !showConfirm;
  const shellRef = useRef(null);
  const scrollRef = useRef(null);
  const cardRef = useCallback((node) => {
    if (node) {
      scrollRef.current = node;
      // Walk up to find .merko-auth-shell
      shellRef.current = node.closest('.merko-auth-shell');
    }
  }, []);
  useAuthViewportLock(shellRef, scrollRef);


  return (
    <div className="merko-form-card">
      <div className="merko-form-card-scroll" ref={cardRef}>

        {/* ── Tablet only: dark branded in-card header strip (hidden on desktop/mobile) ── */}
        <div className="merko-card-brand-strip">
          <div className="merko-card-brand-strip-inner">
            <div className="merko-card-brand-strip-logo">
              <div className="merko-card-brand-strip-icon">
                <Star size={16} fill="white" stroke="white" />
              </div>
              <img src={merkoLogo} alt="Merko"
                style={{ height: 22, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </div>
            <p className="merko-card-brand-strip-tagline">AI-Powered Email Marketing · That Converts</p>
          </div>
        </div>

        <div className="merko-form-content">

          <div className="merko-form-head">
            <div className="merko-form-logo">
              <img src={blackMerkoLogoerkoLogo} alt="Merko" style={{ height: 40, width: 'auto', objectFit: 'contain' }} />
            </div>

            {/* Condensed brand tagline — mobile/tablet only; desktop uses HeroPanel */}
            <div className="merko-brand-tagline">
              <p className="merko-brand-tagline-headline">AI-Powered Email Marketing That Converts</p>
              <p className="merko-brand-tagline-desc">Autonomous AI agents that design, send, and optimize campaigns — 24/7.</p>
            </div>
            <h2 className={`merko-form-title ${titleGradient ? 'merko-form-title--gradient' : ''}`}>
              {heading}
            </h2>
            <p className="merko-form-subtitle">{subheading}</p>
          </div>

          {error && (
            <div className="merko-error" role="alert">
              <AlertCircle size={14} strokeWidth={2.5} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          {resetSuccess && (
            <div className="merko-success">
              <CheckCircle2 size={14} strokeWidth={2.5} style={{ flexShrink: 0 }} />
              Password reset! Signing you in…
            </div>
          )}

          {/* ════════════ SIGN IN ════════════ */}
          {tab === 'signIn' && !fpView && !resetView && (
            <>
              <form onSubmit={handleSignIn} className="merko-form-body" noValidate>
                <div className="merko-field">
                  <label className="merko-label">Email address</label>
                  <IconInput icon={Mail} type="email" placeholder="name@company.com"
                    value={siEmail} onChange={e => { setSiEmail(e.target.value); clearErr(); }}
                    autoComplete="email" required />
                </div>
                <div className="merko-field">
                  <div className="merko-label-row">
                    <label className="merko-label">Password</label>
                    <button type="button" className="merko-link-btn"
                      onClick={() => { setFpView(true); clearErr(); }}>
                      Forgot password?
                    </button>
                  </div>
                  <PasswordInput value={siPassword}
                    onChange={e => { setSiPassword(e.target.value); clearErr(); }}
                    placeholder="Enter your password"
                    autoComplete="current-password" />
                </div>
                <button type="submit" className="merko-btn-primary" disabled={loading}>
                  {loading ? <span className="merko-spinner" /> : (
                    <>Sign in <span className="merko-btn-arrow">→</span></>
                  )}
                </button>
              </form>
              <div className="merko-divider"><span>or continue with</span></div>
              <button type="button" className="merko-btn-google" onClick={handleGoogle}>
                <GoogleIcon /> Continue with Google
              </button>
              <p className="merko-social-proof">Secure · Private · No spam</p>
            </>
          )}

          {/* ════════════ SIGN UP ════════════ */}
          {tab === 'signUp' && !showConfirm && (
            <>
              <form onSubmit={handleSignUp} className="merko-form-body" noValidate>
                <div className="merko-field">
                  <label className="merko-label">Username</label>
                  <IconInput icon={User} type="text" placeholder="Choose a username"
                    value={suUsername} onChange={e => { setSuUsername(e.target.value); clearErr(); }}
                    autoComplete="username" required />
                </div>
                <div className="merko-field">
                  <label className="merko-label">Password</label>
                  <PasswordInput value={suPassword}
                    onChange={e => { setSuPassword(e.target.value); clearErr(); }}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    showStrength />
                </div>
                <div className="merko-field">
                  <label className="merko-label">Confirm password</label>
                  <PasswordInput value={suConfirm}
                    onChange={e => { setSuConfirm(e.target.value); clearErr(); }}
                    placeholder="Repeat your password"
                    autoComplete="new-password" />
                </div>
                <button type="submit" className="merko-btn-primary" disabled={loading}>
                  {loading ? <span className="merko-spinner" /> : (
                    <>Create account <span className="merko-btn-arrow">→</span></>
                  )}
                </button>
              </form>
              <div className="merko-divider"><span>or continue with</span></div>
              <button type="button" className="merko-btn-google" onClick={handleGoogle}>
                <GoogleIcon /> Continue with Google
              </button>
              <p className="merko-tos">
                By creating an account you agree to our{' '}
                <a href="https://merko.ai/terms" className="merko-footer-link" target="_blank" rel="noreferrer">Terms</a>
                {' '}and{' '}
                <a href="https://merko.ai/privacy" className="merko-footer-link" target="_blank" rel="noreferrer">Privacy Policy</a>.
              </p>
            </>
          )}

          {/* ════════════ EMAIL VERIFICATION ════════════ */}
          {tab === 'signUp' && showConfirm && (
            <form onSubmit={handleConfirmSignUp} className="merko-form-body" noValidate>
              <div className="merko-verify-icon">
                <div className="merko-verify-icon-circle">
                  <Mail size={22} strokeWidth={2} color="white" />
                </div>
              </div>
              <div className="merko-field">
                <label className="merko-label">Verification code</label>
                <IconInput icon={KeyRound} type="text" placeholder="● ● ● ● ● ●"
                  value={confirmCode} onChange={e => { setConfirmCode(e.target.value); clearErr(); }}
                  autoComplete="one-time-code" maxLength={6} required center />
              </div>
              <button type="submit" className="merko-btn-primary" disabled={loading}>
                {loading ? <span className="merko-spinner" /> : 'Verify email'}
              </button>
              <button type="button" className="merko-resend-btn" onClick={handleResend}
                disabled={resendCooldown > 0}>
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Didn\'t receive it? Resend code'}
              </button>
              <button type="button" className="merko-back-btn"
                onClick={() => { setShowConfirm(false); clearErr(); }}>
                <ArrowLeft size={14} /> Back to sign up
              </button>
            </form>
          )}

          {/* ════════════ FORGOT PASSWORD ════════════ */}
          {fpView && !resetView && (
            <form onSubmit={handleForgotPassword} className="merko-form-body" noValidate>
              <div className="merko-verify-icon">
                <div className="merko-verify-icon-circle merko-verify-icon-circle--lock">
                  <KeyRound size={22} strokeWidth={2} color="white" />
                </div>
              </div>
              <div className="merko-field">
                <label className="merko-label">Email address</label>
                <IconInput icon={Mail} type="email" placeholder="name@company.com"
                  value={siEmail} onChange={e => { setSiEmail(e.target.value); clearErr(); }}
                  autoComplete="email" required />
              </div>
              <button type="submit" className="merko-btn-primary" disabled={loading}>
                {loading ? <span className="merko-spinner" /> : 'Send reset code'}
              </button>
              <button type="button" className="merko-back-btn"
                onClick={() => { setFpView(false); clearErr(); }}>
                <ArrowLeft size={14} /> Back to sign in
              </button>
            </form>
          )}

          {/* ════════════ CONFIRM RESET ════════════ */}
          {resetView && (
            <form onSubmit={handleConfirmReset} className="merko-form-body" noValidate>
              <div className="merko-verify-icon">
                <div className="merko-verify-icon-circle merko-verify-icon-circle--mail">
                  <Mail size={22} strokeWidth={2} color="white" />
                </div>
              </div>
              <div className="merko-field">
                <label className="merko-label">Reset code</label>
                <IconInput icon={KeyRound} type="text" placeholder="Enter the code from your email"
                  value={resetCode} onChange={e => { setResetCode(e.target.value); clearErr(); }}
                  autoComplete="one-time-code" required />
              </div>
              <div className="merko-field">
                <label className="merko-label">New password</label>
                <PasswordInput value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); clearErr(); }}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  showStrength />
              </div>
              <button type="submit" className="merko-btn-primary" disabled={loading}>
                {loading ? <span className="merko-spinner" /> : 'Reset password'}
              </button>
              <button type="button" className="merko-back-btn"
                onClick={() => { setResetView(false); setFpView(false); clearErr(); }}>
                <ArrowLeft size={14} /> Back to sign in
              </button>
            </form>
          )}

          {/* Account mode switch — shown on mobile/tablet instead of tab bar */}
          {showTabs && (
            <div className="merko-switch-mode">
              {tab === 'signIn' ? (
                <span>
                  Don't have an account?{' '}
                  <button type="button" className="merko-switch-mode-btn"
                    onClick={() => switchTab('signUp')}>
                    Create new
                  </button>
                </span>
              ) : (
                <span>
                  Already have an account?{' '}
                  <button type="button" className="merko-switch-mode-btn"
                    onClick={() => switchTab('signIn')}>
                    Sign in
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* ─ Footer ─ */}
        <div className="merko-card-footer">
          © {new Date().getFullYear()} Merko Inc.
          {/* Hide Privacy · Terms when signUp TOS block already shows them */}
          {!(tab === 'signUp' && !showConfirm) && (
            <>
              &nbsp;·&nbsp;
              <a href="https://merko.ai/privacy" className="merko-footer-link" target="_blank" rel="noreferrer">Privacy</a>
              &nbsp;·&nbsp;
              <a href="https://merko.ai/terms" className="merko-footer-link" target="_blank" rel="noreferrer">Terms</a>
            </>
          )}
        </div>
        <div className="merko-bottom-spacer" aria-hidden="true" />

      </div>
    </div>

  );
}

export function CustomHeader() { return null; }
export function CustomFooter() { return null; }
